import { requireApiOrigin } from "@utils/apiOrigin";
import { authenticatedFetch } from "@utils/authenticatedFetch";

function apiBase(): string {
  return requireApiOrigin();
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string" && body.detail.trim()) return body.detail;
  } catch {
    // non-JSON error body
  }
  return fallback;
}

export interface NiPortfolioRow {
  row_number: number;
  facility_name: string;
  address: string;
  ccn?: string | null;
  npi?: string | null;
  intent: string;
  note?: string | null;
  is_valid: boolean;
  error_reason?: string | null;
  warnings?: string[] | null;
  review_status?: "pending" | "approved" | "disapproved" | "canceled" | "needs_clarification" | "deleted" | null;
  clarification_reason?: string | null;
  portfolio_id?: number | null;
  submitted_at?: string | null;
}

export interface NiPortfolio {
  id: number;
  status: "draft" | "submitted";
  source_filename: string;
  submitted_at?: string | null;
  max_facilities: number;
  row_count: number;
  valid_count: number;
  invalid_count: number;
  parse_token?: string | null;
  rows: NiPortfolioRow[];
}

/** True when the portfolio still lists a facility that was not removed. */
export function portfolioHasVisibleFacilities(
  portfolio: NiPortfolio | null | undefined,
): boolean {
  if (!portfolio) return false;
  const rows = portfolio.rows ?? [];
  if (rows.length > 0) {
    return rows.some((row) => row.review_status !== "deleted");
  }
  return portfolio.row_count > 0;
}

export async function fetchIntelligenceLimits(): Promise<{ max_facilities: number }> {
  const response = await authenticatedFetch(`${apiBase()}/api/v1/network-intelligence/limits`);
  if (!response.ok) {
    throw new Error(await readError(response, "Could not load portfolio capacity"));
  }
  return response.json() as Promise<{ max_facilities: number }>;
}

async function downloadNamedFile(path: string, filename: string, fallback: string): Promise<void> {
  const response = await authenticatedFetch(`${apiBase()}${path}`);
  if (!response.ok) {
    throw new Error(await readError(response, fallback));
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadIntelligenceTemplate(): Promise<void> {
  await downloadNamedFile(
    "/api/v1/network-intelligence/template",
    "network_intelligence_portfolio.xlsx",
    "Could not download template",
  );
}

export async function downloadIntelligenceCsvTemplate(): Promise<void> {
  await downloadNamedFile(
    "/api/v1/network-intelligence/template.csv",
    "network_intelligence_portfolio.csv",
    "Could not download CSV template",
  );
}

export async function parseIntelligencePortfolio(file: File): Promise<NiPortfolio> {
  const form = new FormData();
  form.append("file", file);
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/parse`,
    { method: "POST", body: form },
  );
  if (!response.ok) {
    throw new Error(await readError(response, "Could not read spreadsheet"));
  }
  return response.json() as Promise<NiPortfolio>;
}

export async function fetchSubmittedPortfolio(): Promise<NiPortfolio | null> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/submitted`,
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const message = await readError(response, "Could not load portfolio status");
    if (message.startsWith("NI_NO_SUBMISSION")) return null;
    throw new Error(message);
  }
  return response.json() as Promise<NiPortfolio>;
}

export async function fetchIntelligenceDraft(): Promise<NiPortfolio | null> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/draft`,
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(await readError(response, "Could not load portfolio draft"));
  }
  return response.json() as Promise<NiPortfolio>;
}

export interface NiPortfolioRowUpdate {
  facility_name: string;
  address: string;
  ccn?: string | null;
  npi?: string | null;
  intent: string;
  note?: string | null;
}

export async function updateIntelligenceRow(
  rowNumber: number,
  body: NiPortfolioRowUpdate,
): Promise<NiPortfolio> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/draft/rows/${rowNumber}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
  if (!response.ok) {
    throw new Error(await readError(response, "Could not save facility row"));
  }
  return response.json() as Promise<NiPortfolio>;
}

export async function deleteIntelligenceRow(rowNumber: number): Promise<NiPortfolio> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/draft/rows/${rowNumber}`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error(await readError(response, "Could not remove facility row"));
  }
  return response.json() as Promise<NiPortfolio>;
}

export async function saveIntelligenceDraft(portfolio: NiPortfolio): Promise<NiPortfolio> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/draft`,
    {
      method: "POST",
      body: JSON.stringify({
        source_filename: portfolio.source_filename,
        rows: portfolio.rows,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, "Could not save portfolio draft"));
  }
  return response.json() as Promise<NiPortfolio>;
}

export async function submitIntelligenceDraft(portfolio?: NiPortfolio): Promise<NiPortfolio> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/network-intelligence/portfolios/draft/submit`,
    {
      method: "POST",
      body: JSON.stringify({
        portfolio_id: portfolio && portfolio.id > 0 ? portfolio.id : null,
        source_filename: portfolio?.source_filename,
        rows: portfolio?.rows ?? [],
        parse_token: portfolio?.parse_token ?? null,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, "Could not submit portfolio"));
  }
  return response.json() as Promise<NiPortfolio>;
}
